import { getUuid } from "@src/helpers/functions";
import {
  AppDispatch,
  createKit,
  createKitClear,
  createKitRequest,
  updateKit,
  updateKitClear,
  updateKitRequest,
} from "@src/redux";
import { Form, Input, InputNumber, Select, Row, Col, Button } from "antd";
import React, { useEffect } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

type Props = {
  handleCancel: any;
  mutationType: string;
  modalType: string;
  kit?: any;
  getKitLists: any;
};

const ClientKitForm = (props: Props) => {
  const { handleCancel, modalType, kit, getKitLists } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [form] = Form.useForm();
  const { id, grp_id: grpId } = useParams();
  const { result, loading, error } = useSelector(createKit);

  const {
    result: updateKitResult,
    loading: updateKitLoading,
    error: updateKitError,
  } = useSelector(updateKit);

  useEffect(() => {
    if (kit && modalType === "edit") {
      form.setFieldValue("referenceId", kit?.referenceId);
    } else {
      form.resetFields();
    }
  }, [kit]);

  useEffect(() => {
    if (result?.status === "success") {
      toast.success("Kit Created");
      getKitLists();
      dispatch(createKitClear());
      handleCancel();
    } else if (result?.error?.message) {
      handleCancel();
      const msg = result.error.message;
      toast.error(
        msg.includes("referenceId")
          ? "Unique constraint error on field Reference ID"
          : msg.includes("prisma")
          ? "Something went wrong"
          : msg
      );
      dispatch(createKitClear());
    } else if (updateKitResult?.status === "success") {
      dispatch(updateKitClear());
      handleCancel();
      getKitLists();
      toast.success("Kit Updated");
    } else if (updateKitResult?.error?.message) {
      toast.error(
        updateKitResult?.error?.message.includes("prisma")
          ? "Something went wrong"
          : updateKitResult?.error?.message
      );
      handleCancel();
      dispatch(updateKitClear());
    }
  }, [result, updateKitResult, error, updateKitError]);

  const options = [
    {
      label: "Mobile",
      value: "mobile",
    },
    {
      label: "BLE Gateway",
      value: "ble_gateway",
    },
  ];

  const handleSubmit = (values: any) => {
    if (
      values?.maximumVincenseDevices <= 0 &&
      values?.maximumBpMonitorDevices <= 0 &&
      values?.maximumGlucometerDevices <= 0 &&
      values?.maximumWeighingScaleDevices <= 0 &&
      values?.maximumEcgDevices <= 0
    ) {
      toast.error("Maximum devices should be greater than 0");
      return;
    }
    if (modalType === "add") {
      dispatch(
        createKitRequest({
          data: {
            ...values,
            updateReferenceId: getUuid(),
            organizationId: id,
            groupId: grpId,
          },
        })
      );
    } else {
      dispatch(
        updateKitRequest({
          id: kit?.id,
          data: {
            referenceId: values.referenceId,
          },
        })
      );
    }
  };

  return (
    <Form layout="vertical" onFinish={handleSubmit} form={form}>
      <Row gutter={16}>
        {modalType === "add" && (
          <Col span={12}>
            <Form.Item
              label={t("type")}
              name={"type"}
              rules={[{ required: true, message: t("type_is_required") }]}
            >
              <Select placeholder="" options={options} />
            </Form.Item>
          </Col>
        )}
        <Col span={modalType === "edit" ? 24 : 12}>
          <Form.Item
            label={t("referenceId")}
            name={"referenceId"}
            rules={[{ required: true, message: t("reference_id_is_required") }]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>

      {modalType === "add" && (
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={t("max_vincense_devices")}
              name={"maximumVincenseDevices"}
              rules={[
                { required: true, message: t("max_vincense_is_required") },
              ]}
            >
              <InputNumber
                controls={false}
                changeOnWheel={false}
                style={{
                  width: "100%",
                }}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={t("max_bp_monitor_devices")}
              name={"maximumBpMonitorDevices"}
              rules={[{ required: true, message: t("max_bp_is_required") }]}
            >
              <InputNumber
                controls={false}
                changeOnWheel={false}
                style={{
                  width: "100%",
                }}
              />
            </Form.Item>
          </Col>
        </Row>
      )}

      {modalType === "add" && (
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={t("max_glucometer_devices")}
              name={"maximumGlucometerDevices"}
              rules={[
                {
                  required: true,
                  message: t("max_glucometer_is_required"),
                },
              ]}
            >
              <InputNumber
                controls={false}
                changeOnWheel={false}
                style={{
                  width: "100%",
                }}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={t("max_weighing_scale_devices")}
              name={"maximumWeighingScaleDevices"}
              rules={[
                {
                  required: true,
                  message: t("max_weighing_scale_is_required"),
                },
              ]}
            >
              <InputNumber
                controls={false}
                changeOnWheel={false}
                style={{
                  width: "100%",
                }}
              />
            </Form.Item>
          </Col>
        </Row>
      )}

      {modalType === "add" && (
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={t("max_ecg_devices")}
              name={"maximumEcgDevices"}
              rules={[
                { required: true, message: t("error:max_ecg_is_required") },
              ]}
            >
              <InputNumber
                controls={false}
                changeOnWheel={false}
                style={{
                  width: "100%",
                }}
              />
            </Form.Item>
          </Col>
        </Row>
      )}
      <div className="kit-edit-modal-footer">
        <Button onClick={handleCancel} danger>
          {t("close")}
        </Button>
        <Form.Item style={{ marginBottom: 0 }}>
          <Button
            htmlType="submit"
            type="primary"
            loading={updateKitLoading || loading}
          >
            {t("submit")}
          </Button>
        </Form.Item>
      </div>
    </Form>
  );
};

export default ClientKitForm;
